// App.js
import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import './styles/Main.css';

// Auth imports
import { useUser } from './features/auth/hooks/useUser';
import { ProtectedRoute } from './features/auth/components/ProtectedRoute';
import { AdminRoute } from './features/auth/components/AdminRoute';
import OAuthCallback from './features/auth/components/OAuthCallback';
import CognitoByocCallback from './features/auth/components/CognitoByocCallback';

import { useOrganizationBucketSync } from './shared/integration/hooks/useOrganizationBucketSync';

// Session management
import SessionModalProvider from './shared/providers/SessionModalProvider';
import { ConfirmationProvider } from './shared/modals/confirmation';

// Services and utilities
import { smartWarmupService } from './shared/services/auth/SmartWarmupService';
import OrganizationPreloader from './features/organization/utils/OrganizationPreloader';
import MainLayout from './shared/layout/MainLayout';
import ErrorBoundary from './shared/components/ErrorBoundary';
import {
  DashboardErrorBoundary,
  AutomationScriptsErrorBoundary,
  QualityIntelligenceErrorBoundary,
  ProjectGenerationErrorBoundary,
  OrganizationErrorBoundary,
  TokenUsageErrorBoundary,
  ProjectsErrorBoundary
} from './shared/components/FeatureErrorBoundary';
import PostLoginLoader from './features/auth/components/PostLoginLoader';
import PostLoginLoaderBubbles from './features/auth/components/PostLoginLoaderBubbles';
import PostLoginLoaderCircuits from './features/auth/components/PostLoginLoaderCircuits';
import AppSuspenseFallback from './shared/components/AppSuspenseFallback';
import DivisionSwitchLoaderPreview from './features/organization/components/DivisionSwitchLoaderPreview';
import RunStreamInit from './shared/components/RunStreamInit';
import BackgroundGenerationReconnector from './shared/components/BackgroundGenerationReconnector';
import BackgroundAnalyserReconnector from './shared/components/BackgroundAnalyserReconnector';

// Eager-loaded screens (needed immediately)
import HomeScreenWithForms from './features/auth/pages/HomeScreen';
import BackgroundChatListener from './features/qualityIntelligence/components/analyzer/DeepAnalyserComponent/BackgroundChatListener';
import XRayExportPollerInit from './features/qualityIntelligence/components/analyzer/DeepAnalyserComponent/XRayExportPollerInit';
import GlobalToastNotifications from './shared/notifications/GlobalToastNotifications';
import BackgroundStreamNotifier from './shared/notifications/BackgroundStreamNotifier';
import { ToastNotificationProvider } from './shared/notifications/ToastNotificationContext';

// =============================================================
// LAZY-LOADED ROUTE COMPONENTS (~2-3MB saved from initial bundle)
// =============================================================

// Authentication screens (lazy)
const AcceptInvitePage = lazy(() => import('./features/auth/pages/AcceptInvitePage'));
const IntegrationSetupWrapper = lazy(() => import('./features/auth/pages/integrations/IntegrationSetupWrapper'));

// Organization setup screens (lazy)
const OrganizationSetup = lazy(() => import('./features/organization/pages/SetupPage/SetupPage'));
const RolesManager = lazy(() => import('./features/organization/pages/RolesPage/RoleManager'));
const DivisionsManager = lazy(() => import('./features/organization/pages/DivisionsPage/DivisionManager'));
const DivisionDetail = lazy(() => import('./features/organization/pages/DivisionsPage/DivisionDetail'));
const DeletedDivisionsManager = lazy(() => import('./features/organization/pages/DivisionsPage/DeletedDivisionsManager'));
const UsersManager = lazy(() => import('./features/organization/pages/MembersPage/MemberManager'));

// Dashboard and project screens (lazy)
const DashboardScreen = lazy(() => import('./features/dashboard/pages/DashboardScreen'));
const AutomationPage = lazy(() => import('./features/automationScripts/pages/AutomationPage'));
const ProjectView = lazy(() => import('./features/projects/pages/ProjectView'));
const ProjectDetailView = lazy(() => import('./features/projects/pages/ProjectDetailView'));
const GroupingDetailPage = lazy(() => import('./features/executionStudio/pages/GroupingDetailPage'));
const TestCaseSplitPage = lazy(() => import('./features/projects/pages/TestCaseSplitPage'));
const ProjectFolderSplitPage = lazy(() => import('./features/projects/pages/ProjectFolderSplitPage'));
const RequirementsRepositoryPage = lazy(() => import('./features/projects/pages/RequirementsRepositoryPage'));
const BusinessProcessesRepositoryPage = lazy(() => import('./features/projects/pages/BusinessProcessesRepositoryPage'));
const TraceabilityMatrixRepositoryPage = lazy(() => import('./features/projects/pages/TraceabilityMatrixRepositoryPage'));
const RiskAnalysisRepositoryPage = lazy(() => import('./features/projects/pages/RiskAnalysisRepositoryPage'));
const ProjectFileViewerPage = lazy(() => import('./features/projects/pages/ProjectFileViewerPage'));
const TrashPage = lazy(() => import('./features/projects/pages/TrashPage'));

// AI screens (lazy)
const ProjectChatScreen = lazy(() => import('./features/scriptGeneration/pages/ProjectChatScreen'));
// TEMPORARY: mock design harness for the generation-screens redesign — remove with its route below when done.
// const MockProjectChatScreen = lazy(() => import('./features/scriptGeneration/pages/MockProjectChatScreen'));
const DataAnalyzerChat = lazy(() => import('./features/qualityIntelligence/components/analyzer/DeepAnalyserComponent/DataAnalyzerChat'));
const DataAnalyzerHome = lazy(() => import('./features/qualityIntelligence/pages/DataAnalyzerHome'));
const AnalystCapabilitiesWrapper = lazy(() => import('./features/qualityIntelligence/components/AnalystCapabilitiesWrapper'));
const ChatHistoryView = lazy(() => import('./features/qualityIntelligence/pages/ChatHistoryView'));

// Token usage (lazy)
const TokenUsageDashboard = lazy(() => import('./features/tokenUsage/pages/TokenUsageDashboard'));

// Service Worker update notification (lazy)
const ServiceWorkerUpdateNotification = lazy(() => import('./shared/components/ServiceWorkerUpdateNotification'));


/**
 * Backward-compat redirect: /test-suite/:projectId/* → /automation-script/:projectId/*
 */
const TestSuiteRedirect = () => {
  const { projectId, '*': splat } = useParams();
  const location = useLocation();
  const newPath = `/automation-script/${projectId}${splat ? `/${splat}` : ''}`;
  return <Navigate to={newPath} state={location.state} replace />;
};

/**
 * Backward-compat redirect: /organization-divisions/:divisionId → .../settings
 */
const DivisionDetailRedirect = () => {
  const { divisionId } = useParams();
  return <Navigate to={`/organization-divisions/${divisionId}/settings`} replace />;
};

// Shared /callback route — Microsoft state: "<csrfToken>|<clientId>", BYOC Cognito state: "byoc|<base64email>"
const CallbackRouter = () => {
  const { search } = useLocation();
  const state = new URLSearchParams(search).get('state');
  if (state && state.includes('|') && !state.startsWith('byoc|')) {
    return <OAuthCallback provider="microsoft" />;
  }
  return <CognitoByocCallback />;
};

// Folder route dispatcher — a few repositories get the richer preview-first
// layout (DocumentRepoSplitPage); everything else uses the generic folder view.
const REPO_PAGE_BY_FOLDER = {
  'Business Processes Repository': BusinessProcessesRepositoryPage,
  'Traceability Matrix Repository': TraceabilityMatrixRepositoryPage,
  'Risk Analysis Repository': RiskAnalysisRepositoryPage,
};

const FolderRouter = () => {
  const params = useParams();
  const folderName = decodeURIComponent(params['*'] || '').replace(/\/$/, '');
  const RepoPage = REPO_PAGE_BY_FOLDER[folderName];
  if (RepoPage) {
    return <RepoPage />;
  }
  return <ProjectFolderSplitPage />;
};

const IntegrationSetupWithUserData = () => {
  const { userData } = useUser();

  return (
    <IntegrationSetupWrapper
      organizationId={userData.tenantId}
      userId={userData.userId}
    />
  );
};

// Component that syncs bucket config for all authenticated routes
const AppContent = ({ children }) => {
  // This hook syncs the custom bucket configuration from backend
  useOrganizationBucketSync();
  
  return <>{children}</>;
};

function App() {
  useEffect(() => {
    // Initialize services
    smartWarmupService.init();
    
    const handleVisibilityChange = () => {
      if (!document.hidden && !smartWarmupService.isAPIWarmedUp()) {
        smartWarmupService.warmupAPI();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <ServiceWorkerUpdateNotification />
      </Suspense>
      {/* Zustand stores are global - no providers needed for Theme, User, Projects, Dashboard, Generation */}
      <AppContent>
        <ToastNotificationProvider>
          <ConfirmationProvider>
            <BrowserRouter>
              <SessionModalProvider>
                <OrganizationPreloader />
                <BackgroundChatListener />
                <XRayExportPollerInit />
                <RunStreamInit />
                <BackgroundGenerationReconnector />
                <BackgroundAnalyserReconnector />
                <GlobalToastNotifications />
                <BackgroundStreamNotifier />
                    
                    <ErrorBoundary componentName="Application">
                    <Suspense fallback={<AppSuspenseFallback />}>
                    <Routes>
                      {/* Authentication Pages */}
                      <Route path="/" element={<HomeScreenWithForms />} />
                      
                      {/* OAuth Callback Routes */}
                      <Route path="/callback" element={<CallbackRouter />} />
                      <Route path="/google-callback" element={<OAuthCallback provider="google" />} />
                      
                      {/* Accept Invite Page */}
                      <Route path="/acceptinvite" element={<AcceptInvitePage />} />

                      {/* DEV PREVIEW ONLY: view post-login transition screens without logging in — remove before shipping */}
                      {process.env.NODE_ENV === 'development' && (
                        <>
                          <Route path="/dev/oauth-callback-preview" element={<OAuthCallback provider="microsoft" skipProcessing />} />
                          <Route path="/dev/post-login-loader-preview" element={<PostLoginLoader />} />
                          <Route path="/dev/post-login-loader-bubbles-preview" element={<PostLoginLoaderBubbles />} />
                          <Route path="/dev/post-login-loader-circuits-preview" element={<PostLoginLoaderCircuits />} />
                          <Route path="/dev/division-switch-loader-preview" element={<DivisionSwitchLoaderPreview />} />
                        </>
                      )}

                      {/* All Protected Routes */}
                      <Route element={<ProtectedRoute />}>
                        {/* Integration Setup - no MainLayout (standalone page) */}
                        <Route path="/integration-setup" element={<IntegrationSetupWithUserData />} />

                        {/* All routes with MainLayout - renders once, only content swaps */}
                        <Route element={<MainLayout />}>
                          {/* Dashboard - New main dashboard */}
                          <Route path="/dashboard" element={
                            <DashboardErrorBoundary>
                              <DashboardScreen />
                            </DashboardErrorBoundary>
                          } />
                          {/* Organization Pages */}
                          <Route path="/organization-settings" element={
                            <OrganizationErrorBoundary>
                              <OrganizationSetup />
                            </OrganizationErrorBoundary>
                          } />

                          <Route path="/organization-roles-and-permissions" element={
                            <OrganizationErrorBoundary>
                              <RolesManager />
                            </OrganizationErrorBoundary>
                          } />

                          <Route path="/organization-divisions" element={
                            <OrganizationErrorBoundary>
                              <DivisionsManager />
                            </OrganizationErrorBoundary>
                          } />

                          <Route path="/organization-divisions/deleted" element={
                            <OrganizationErrorBoundary>
                              <DeletedDivisionsManager />
                            </OrganizationErrorBoundary>
                          } />

                          <Route path="/organization-divisions/:divisionId" element={<DivisionDetailRedirect />} />

                          <Route path="/organization-divisions/:divisionId/settings" element={
                            <OrganizationErrorBoundary>
                              <DivisionDetail />
                            </OrganizationErrorBoundary>
                          } />

                          <Route path="/organization-divisions/:divisionId/members" element={
                            <OrganizationErrorBoundary>
                              <DivisionDetail />
                            </OrganizationErrorBoundary>
                          } />

                          {/* Tenant-admin only — the sidebar link is hidden for others, this blocks direct URL access */}
                          <Route element={<AdminRoute />}>
                            <Route path="/organization-members" element={
                              <OrganizationErrorBoundary>
                                <UsersManager />
                              </OrganizationErrorBoundary>
                            } />
                          </Route>

                          {/* Project Pages */}
                          <Route path="/projects" element={
                            <ProjectsErrorBoundary>
                              <ProjectView />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/automation-scripts" element={
                            <ProjectsErrorBoundary>
                              <ProjectDetailView />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/test-suites/:suiteId" element={
                            <ProjectsErrorBoundary>
                              <GroupingDetailPage />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/test-case-repository" element={
                            <ProjectsErrorBoundary>
                              <TestCaseSplitPage />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/test-case-repository/*" element={
                            <ProjectsErrorBoundary>
                              <TestCaseSplitPage />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/requirements-repository" element={
                            <ProjectsErrorBoundary>
                              <RequirementsRepositoryPage />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/folder/*" element={
                            <ProjectsErrorBoundary>
                              <FolderRouter />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/folder-file/*" element={
                            <ProjectsErrorBoundary>
                              <ProjectFileViewerPage />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId/trash" element={
                            <ProjectsErrorBoundary>
                              <TrashPage />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/projects/:projectId" element={
                            <ProjectsErrorBoundary>
                              <ProjectDetailView />
                            </ProjectsErrorBoundary>
                          } />

                          <Route path="/automation-script/:projectId/*" element={
                            <AutomationScriptsErrorBoundary>
                              <AutomationPage />
                            </AutomationScriptsErrorBoundary>
                          } />

                          {/* Backward compat redirect: old /test-suite path → new /automation-script path */}
                          <Route path="/test-suite/:projectId/*" element={
                            <TestSuiteRedirect />
                          } />

                          {/* GenAI Pages */}
                          <Route path="/project-chat/:conversationId" element={
                            <ProjectGenerationErrorBoundary>
                              <ProjectChatScreen />
                            </ProjectGenerationErrorBoundary>
                          } />

                          {/* TEMPORARY: mock design harness — remove this route + MockProjectChatScreen when redesign is done */}
                          {/* <Route path="/project-chat-mock" element={
                            <ProjectGenerationErrorBoundary>
                              <MockProjectChatScreen />
                            </ProjectGenerationErrorBoundary>
                          } /> */}

                          <Route path="/token-usage" element={
                            <TokenUsageErrorBoundary>
                              <TokenUsageDashboard />
                            </TokenUsageErrorBoundary>
                          } />

                          <Route path="/data-analyzer-chat" element={
                            <QualityIntelligenceErrorBoundary>
                              <DataAnalyzerChat />
                            </QualityIntelligenceErrorBoundary>
                          } />

                          <Route path="/data-analyzer-home" element={<Navigate to="/capabilities" replace />} />

                          <Route path="/capabilities" element={
                            <QualityIntelligenceErrorBoundary>
                              <AnalystCapabilitiesWrapper />
                            </QualityIntelligenceErrorBoundary>
                          } />

                          <Route path="/chat-history" element={
                            <QualityIntelligenceErrorBoundary>
                              <ChatHistoryView />
                            </QualityIntelligenceErrorBoundary>
                          } />

                          <Route path="/data-analyzer" element={
                            <QualityIntelligenceErrorBoundary>
                              <DataAnalyzerChat />
                            </QualityIntelligenceErrorBoundary>
                          } />
                        </Route>
                      </Route>

                      {/* Catch all route */}
                      <Route path="*" element={<HomeScreenWithForms />} />
                    </Routes>
                    </Suspense>
                    </ErrorBoundary>
              </SessionModalProvider>
            </BrowserRouter>
          </ConfirmationProvider>
        </ToastNotificationProvider>
      </AppContent>
    </>
  );
}

export default App;